import { AllLocaleData, AllMessages } from '@lingui/core'
import { CircularProgress, CssBaseline, ThemeProvider } from '@mui/material'
import { OpenAPI } from '@om1/aspen-api/codegen/client/core/OpenAPI'
import { platformConfigActions } from '@om1/platform-config/state'
import { NotificationState } from '@om1/platform-notifications'
import { TrackingProvider, createTrackingMiddleware, useAnalytics } from '@om1/platform-tracking'
import { createHelpDialog } from '@om1/platform-ui-kit/src/components/sidebar/HelpDialog'
import { createTimeoutDialog } from '@om1/platform-ui-kit/src/components/sidebar/TimeoutDialog'
import { platformTheme } from '@om1/platform-ui-kit/src/theme'
import { withConfig } from '@om1/platform-utils'
import { ConnectedRouter, routerMiddleware } from 'connected-react-router'
import { createBrowserHistory } from 'history'
import { en, es } from 'make-plural'
import { SnackbarProvider, useSnackbar } from 'notistack'
import React, { useEffect } from 'react'
import { Provider } from 'react-redux'
import { addMiddleware } from 'redux-dynamic-middlewares'
import createSagaMiddleware, { SagaMiddleware } from 'redux-saga'
import packageJson from '../package.json'
import { AppStore, createAppStore, createReducer, createRootSaga } from './aspen-state'
import { ConfigInterface, config } from './config/AspenConfig'
import './index.css'
import { messages as enMessages } from './locales/en/messages'
import { messages as esMessages } from './locales/es/messages'
import LandingPageComponent from './pages/LandingPage'
import { Auth0AspenProvider, useAspenAuth } from './providers/Auth0AspenProvider'
import { LocaleEnum } from './shared/providers/internationalization/language-config'
import LanguageProvider from './shared/providers/internationalization/LanguageProvider'

function App() {
    // create top level react contexts (router history, saga middleware, redux store etc)
    const history = createBrowserHistory()
    const appReducer = createReducer(history)
    const sagaMiddleware = createSagaMiddleware()
    const store = createAppStore(appReducer, sagaMiddleware, routerMiddleware(history))

    return (
        <Provider store={store}>
            <ConnectedRouter history={history}>
                <Auth0AspenProvider>
                    <SnackbarProvider maxSnack={3}>
                        <TrackingProvider>
                            <ConfiguredApp sagaMiddleware={sagaMiddleware} store={store} />
                        </TrackingProvider>
                    </SnackbarProvider>
                </Auth0AspenProvider>
            </ConnectedRouter>
        </Provider>
    )
}

interface ConfiguredAppComponentProps {
    config: ConfigInterface
    sagaMiddleware: SagaMiddleware<object>
    store: AppStore
}

const ConfiguredAppComponent: React.FunctionComponent<ConfiguredAppComponentProps> = ({
    config,
    sagaMiddleware,
    store
}: ConfiguredAppComponentProps) => {
    let { aspenApiUrl, writeKey, buildNumber, environmentTag } = config
    const { isLoading, isAuthenticated, user, getAccessTokenSilently } = useAspenAuth()
    OpenAPI.BASE = aspenApiUrl.replace('/aspen', '')

    const { enqueueSnackbar } = useSnackbar()
    const analytics = useAnalytics(writeKey)

    // create tracking middleware and add to redux
    const trackingMiddleware = createTrackingMiddleware(analytics)
    addMiddleware(trackingMiddleware)

    // start redux-sagas
    const rootsaga = createRootSaga(enqueueSnackbar, analytics)
    const [firstLoad, setFirstLoad] = React.useState(true)

    if (rootsaga && firstLoad) {
        sagaMiddleware.run(rootsaga)
        setFirstLoad(false)
    }

    // manages the auth0 authentication status, issues actions based on changes
    if (user) {
        // manages versioning and environment tags
        // store.dispatch(falconApiActions.getFalconVersion())
        store.dispatch(platformConfigActions.setBuildNumber(buildNumber))
        store.dispatch(platformConfigActions.setEnvironmentTag(environmentTag))
        store.dispatch(platformConfigActions.setSemanticVersion(packageJson.version))
    }

    useEffect(() => {
        if (isAuthenticated) {
            const setAccessToken = async () => {
                try {
                    const token = await getAccessTokenSilently()
                    OpenAPI.TOKEN = token
                } catch (error) {
                    // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
                    console.error('Error setting access token:', error)
                }
            }
            setAccessToken()
        }
    }, [isAuthenticated, getAccessTokenSilently])

    if (isLoading) {
        return (
            <div className='min-h-screen bg-gray-100 flex items-center justify-center'>
                <div className='text-center'>
                    <CircularProgress />
                </div>
            </div>
        )
    }

    // establish translations
    const messages: AllMessages = {
        en: enMessages,
        es: esMessages
    }
    const plurals: AllLocaleData = {
        en: { plurals: en },
        es: { plurals: es }
    }

    const HelpDialog = createHelpDialog<{ notification: NotificationState }>()
    const TimeoutDialog = createTimeoutDialog<{ notification: NotificationState }>()

    return (
        <React.StrictMode>
            <LanguageProvider language={LocaleEnum.English} messages={messages} plurals={plurals}>
                <ThemeProvider theme={platformTheme}>
                    <CssBaseline />
                    <HelpDialog />
                    <TimeoutDialog />
                    <LandingPageComponent />
                </ThemeProvider>
            </LanguageProvider>
        </React.StrictMode>
    )
}

const ConfiguredApp = withConfig<ConfiguredAppComponentProps>(ConfiguredAppComponent, { ...config })

export default App
